import React, { useState, useEffect, useMemo, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import Input from "components/Input";
import {
  getSaveTemplatePeriodDetails,
  SCHEDULER_TEMPLATE_NAME_MAX_LENGTH,
} from "../EventShiftsView/eventsWorkspaceSaveTemplate.utils";
import styles from "./PublishSchedulerEventsConfirmationModal.module.scss";

/**
 * Modal for naming and saving the current Day/Week/Month as a scheduler template.
 * Visual design matches the Save Week as Template mock (underline name field + green primary).
 */
function SaveSchedulerTemplateModal({
  t,
  isOpen,
  isLoading,
  calendarViewId,
  currentDate,
  onConfirm,
  onClose,
}) {
  const periodDetails = useMemo(
    () => getSaveTemplatePeriodDetails(calendarViewId, currentDate),
    [calendarViewId, currentDate],
  );

  const [templateName, setTemplateName] = useState("");
  const [templateError, setTemplateError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setTemplateName("");
    setTemplateError("");
  }, [isOpen]);

  const handleNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (templateError && value.trim().length) {
        setTemplateError("");
      }
      setTemplateName(value);
    },
    [templateError],
  );

  const handleSave = useCallback(async () => {
    if (isLoading) return;

    const trimmed = templateName.trim();
    if (!trimmed) {
      setTemplateError(
        t("form.errors.emptyField", {
          field: t("scheduler.templateName").toLowerCase(),
        }),
      );
      return;
    }
    if (trimmed.length > SCHEDULER_TEMPLATE_NAME_MAX_LENGTH) {
      setTemplateError(t("scheduler.templateNameMaxLength"));
      return;
    }

    await onConfirm?.(trimmed);
  }, [isLoading, templateName, t, onConfirm]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="modal-dialog-centered"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={isLoading ? undefined : onClose}>
        <img src={crossIcon} alt="" />
      </span>
      <ModalBody className={styles["publish-modal-body"]}>
        <Text
          size="25px"
          marginBottom="16px"
          weight="500"
          align="left"
          color="#111b45"
        >
          {t(periodDetails.titleKey)}
        </Text>
        <p className={styles["publish-modal-message"]}>
          {t(periodDetails.introKey, { period: periodDetails.periodValue })}
        </p>
        <Input
          Classes="mb-4"
          Title={t("scheduler.templateName")}
          Type="text"
          Placeholder={t("scheduler.templateNamePlaceholder")}
          Name="templateName"
          HandleChange={handleNameChange}
          Value={templateName}
          Error={templateError}
          MaxLength={SCHEDULER_TEMPLATE_NAME_MAX_LENGTH}
          Disabled={isLoading}
        />
        <div className={styles["publish-modal-actions"]}>
          <button
            type="button"
            className="button button-round button-shadow"
            disabled={isLoading}
            onClick={handleSave}
          >
            {t("scheduler.saveTemplate")}
          </button>
          <button
            type="button"
            className="button button-round button-border button-dark"
            disabled={isLoading}
            onClick={onClose}
          >
            {t("cancel")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SaveSchedulerTemplateModal);
